import dayjs from 'dayjs';
import { DATE_FORMAT } from 'config/api.config';

export const formatters = {

  date: (date: string | Date | null | undefined, format: string = DATE_FORMAT.display): string => {
    if (!date) return '-';
    return dayjs(date).format(format);
  },

  dateTime: (date: string | Date | null | undefined): string => {
    if (!date) return '-';
    return dayjs(date).format(DATE_FORMAT.displayWithTime);
  },

  dateForApi: (date: string | Date): string => {
    return dayjs(date).format(DATE_FORMAT.api);
  },

  dateTimeForApi: (date: string | Date): string => {
    return dayjs(date).format(DATE_FORMAT.apiWithTime);
  },

  timeAgo: (date: string | Date): string => {
    const now = dayjs();
    const target = dayjs(date);
    const diffMinutes = now.diff(target, 'minute');
    const diffHours = now.diff(target, 'hour');
    const diffDays = now.diff(target, 'day');

    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return formatters.date(date);
  },

  formatDateTime: (date: string | Date | null | undefined): string => {
    if (!date) return '-';
    return dayjs(date).format(DATE_FORMAT.displayWithTime);
  },

  duration: (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  },

  minutesToTime: (minutes: number): string => {
    if (minutes === 0) return 'Сразу';
    if (minutes < 60) return `${minutes} мин`;
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours < 24) {
      return mins > 0 ? `${hours}ч ${mins}мин` : `${hours}ч`;
    }
    
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    if (remainingHours > 0) {
      return `${days}д ${remainingHours}ч`;
    }
    return `${days}д`;
  },

  number: (value: number | null | undefined, decimals: number = 0): string => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  },

  currency: (value: number | null | undefined, currency: string = 'USD'): string => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency,
    });
  },

  percent: (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(decimals)}%`;
  },

  fileSize: (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  },

  truncate: (text: string | null | undefined, length: number = 50): string => {
    if (!text) return '';
    if (text.length <= length) return text;
    return `${text.slice(0, length)}...`;
  },

  capitalize: (text: string | null | undefined): string => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  },

  capitalizeWords: (text: string | null | undefined): string => {
    if (!text) return '';
    return text.replace(/\b\w/g, char => char.toUpperCase());
  },

  snakeToTitle: (text: string): string => {
    return text
      .split('_')
      .map(word => formatters.capitalize(word))
      .join(' ');
  },

  phone: (phone: string | null | undefined): string => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{1,3})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
    }
    return phone;
  },

  bundleId: (bundleId: string | null | undefined): string => {
    if (!bundleId) return '-';
    return bundleId;
  },

  boolean: (value: boolean | null | undefined, trueText: string = 'Yes', falseText: string = 'No'): string => {
    if (value === null || value === undefined) return '-';
    return value ? trueText : falseText;
  },

  status: (status: string | null | undefined): string => {
    if (!status) return '-';
    return formatters.snakeToTitle(status);
  },

  json: (data: any): string => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return '{}';
    }
  },

  ctr: (clicked: number, sent: number): string => {
    if (!sent || sent === 0) return '0%';
    const rate = (clicked / sent) * 100;
    return formatters.percent(rate);
  },

  conversionRate: (conversions: number, total: number): string => {
    if (!total || total === 0) return '0%';
    const rate = (conversions / total) * 100;
    return formatters.percent(rate);
  },

  plural: (count: number, singular: string, plural?: string): string => {
    if (count === 1) return `${count} ${singular}`;
    return `${count} ${plural || `${singular}s`}`;
  },

  list: (items: string[], limit: number = 3): string => {
    if (!items || items.length === 0) return '-';
    if (items.length <= limit) return items.join(', ');
    const displayed = items.slice(0, limit);
    const remaining = items.length - limit;
    return `${displayed.join(', ')} +${remaining} more`;
  },

  errorMessage: (error: any): string => {
    if (typeof error === 'string') return error;
    if (error?.response?.data?.error) return error.response.data.error;
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    return 'An unexpected error occurred';
  },

  firebaseConfig: (config: any): string => {
    if (!config) return '-';
    return `Project: ${config.project_id || '-'}`;
  },

  userRole: (role: string): string => {
    const roleMap: Record<string, string> = {
      admin: 'Administrator',
      executor: 'Executor',
      viewer: 'Viewer',
    };
    return roleMap[role] || role;
  },

  appType: (type: string): string => {
    const typeMap: Record<string, string> = {
      'Android/IOS': 'Mobile',
      'PWA': 'Web App',
    };
    return typeMap[type] || type;
  },
};